import { useEffect, useState } from 'react';
import { Alert, Switch, Tag } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import endpoints from '../../api/endpoints';
import { useAppNotifications } from '../../utils/notify';

// Stage 5M: per-user MFA requirement toggle. Mounted inside the master
// view of UserDetailsPanel for sub-users (never for the master's own
// profile — the master's MFA setup is in MasterMfaSection).
//
// The switch flips users.mfa_required on the target user. Enabling
// requires the master to have completed tenant MFA setup AND have it
// active — the backend rejects otherwise with MFA_NOT_CONFIGURED, which
// notify.js maps to a friendly modal.

export default function UserMfaSection({ user, onChanged }) {
  const { notifyError, notifySuccess } = useAppNotifications();

  const [tenantStatus, setTenantStatus] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [required, setRequired] = useState(user.mfaRequired === true);

  useEffect(() => {
    setRequired(user.mfaRequired === true);
  }, [user.mfaRequired, user.id]);

  // Pull the tenant MFA status so we can disable the toggle (and show
  // a helpful explanation) when the master has not yet finished setup.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTenantLoading(true);
      try {
        const data = await endpoints.mfa.getStatus();
        if (cancelled) return;
        setTenantStatus(data);
      } catch (err) {
        // Non-blocking — surface a single warning and let the user
        // retry by re-opening the panel.
        if (!cancelled) {
          notifyError(err, {
            title: 'Could not load MFA status',
            description: 'Per-user MFA toggles may be disabled.'
          });
        }
      } finally {
        if (!cancelled) setTenantLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tenantReady =
    tenantStatus &&
    tenantStatus.isSetupComplete === true &&
    tenantStatus.isActive === true;

  async function toggle(next) {
    if (busy) return;
    setBusy(true);
    // Optimistic — flip locally, roll back on error.
    setRequired(next);
    try {
      await endpoints.users.setMfaRequired(user.id, { mfaRequired: next });
      notifySuccess(
        next ? 'MFA required' : 'MFA not required',
        next
          ? 'This user must enter the 6-digit Authenticator code at login.'
          : 'This user can sign in with just password.'
      );
      if (onChanged) onChanged();
    } catch (err) {
      setRequired(!next);
      notifyError(err, { title: 'Could not change MFA requirement' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded-xl border border-surface-200 bg-white p-3.5">
      <div className="flex items-start gap-2 mb-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
          <SafetyCertificateOutlined />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-ink-900 m-0 leading-tight">
            Multi-factor authentication
          </h3>
          <p className="text-xs text-ink-500 m-0 mt-0.5">
            When enabled, this user must enter the current Google
            Authenticator code controlled by the Master account.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-lg border border-surface-200 bg-surface-50/40 px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          {required ? (
            <Tag color="blue" className="!m-0 !leading-5">MFA required</Tag>
          ) : (
            <Tag className="!m-0 !leading-5">MFA not required</Tag>
          )}
          <span className="text-sm text-ink-700 truncate">
            Require MFA at login
          </span>
        </div>
        <Switch
          checked={required}
          disabled={busy || tenantLoading || (!required && !tenantReady)}
          loading={busy}
          onChange={toggle}
        />
      </div>

      {!tenantLoading && !tenantReady ? (
        <Alert
          type="warning"
          showIcon
          className="mt-3 !py-1.5 !text-[12px]"
          message="Complete Master MFA setup before enabling MFA for users."
          description={
            tenantStatus && tenantStatus.isSetupComplete
              ? 'Master MFA is configured but inactive. Activate it from the master profile, then enable per-user MFA.'
              : 'Open the master profile, scan the QR with Google Authenticator, then verify the code to complete setup.'
          }
        />
      ) : null}
    </div>
  );
}
